<template>
  <v-app-bar color="primary" elevation="2">
    <v-icon icon="mdi-map-search" class="ml-4 mr-2"></v-icon>
    <v-app-bar-title class="text-h6">{{ $t('catalogo.titulo') }}</v-app-bar-title>
    
    <v-spacer></v-spacer>

    <!-- Selector de Idiomas -->
    <v-menu>
      <template v-slot:activator="{ props }">
        <v-btn variant="text" v-bind="props" class="mr-4">
          <v-icon start>mdi-translate</v-icon>
          {{ $i18n.locale.toUpperCase() }}
        </v-btn>
      </template>
      <v-list density="compact">
        <v-list-item @click="$i18n.locale = 'es'">
          <v-list-item-title>Español</v-list-item-title>
        </v-list-item>
        <v-list-item @click="$i18n.locale = 'en'">
          <v-list-item-title>English</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
  </v-app-bar>

  <v-main class="bg-grey-lighten-4">
    <v-container fluid class="px-2 pt-2">
      <v-stepper v-model="step" :items="stepperItems" hide-actions>
        
        <!-- Headers Personalizados -->
      <template v-slot:[`title.1`]>
        {{ $t('catalogo.pasos.zonas') }} 
        <span v-if="zonas.length" class="text-primary text-caption ml-2">({{ zonas.join(', ') }})</span>
      </template>

      <!-- Título Paso 2 -->
      <template v-slot:[`title.2`]>
        {{ $t('catalogo.pasos.granularidad') }} 
        <span v-if="granularidad" class="text-primary text-caption ml-2">({{ $t(`catalogo.granularidadOpciones.${granularidad}`) }})</span>
      </template>



        <!-- Paso 1: Mapa -->
        <template v-slot:[`item.1`]>
          <v-card variant="flat" min-height="550">
            <v-card-item>
              <v-card-title class="text-h5 font-weight-bold">{{ $t('catalogo.zonas.titulo') }}</v-card-title>
              <v-card-subtitle>{{ $t('catalogo.zonas.subtitulo') }}</v-card-subtitle>
            </v-card-item>

            <div class="pa-6">
              <div class="d-flex justify-center mb-4" style="min-height: 48px; gap: 8px;"> 
                <v-chip
                  v-for="zonaItem in zonasFiltradas"
                  :key="zonaItem"
                  color="primary"
                  variant="flat"
                  size="large"
                  closable
                  @click:close="removeZona(zonaItem)"
                >
                  <strong>{{ zonaItem }}</strong>
                </v-chip>
              </div>
              
              <div class="map-wrapper">
                <div id="map" ref="mapContainer" class="entsoe-map-container"></div>
              </div>
            </div>
          </v-card>
        </template>

        <!-- Paso 2: Granularidad -->
        <template v-slot:[`item.2`]>
          <v-card variant="flat" class="pa-4" min-height="550">
            <v-card-title class="text-h5 font-weight-bold">{{ $t('catalogo.granularidad.titulo') }}</v-card-title>
            <v-card-text>
              <p class="mb-4">{{ $t('catalogo.granularidad.instruccion', { zonas: zonas.join(', ') }) }}</p>
              <v-radio-group v-model="granularidad" inline>
                <v-radio :label="$t('catalogo.granularidadOpciones.anual')" value="anual" color="primary" class="mr-8"></v-radio>
                <v-radio :label="$t('catalogo.granularidadOpciones.horaria')" value="horaria" color="primary"></v-radio>
              </v-radio-group>
            </v-card-text>
          </v-card>
        </template>

        <!-- Paso 3: Escenarios -->
        <template v-slot:[`item.3`]>
          <v-card variant="flat" class="pa-4">
            <v-card-title class="text-h5 font-weight-bold">{{ $t('catalogo.escenarios.titulo') }}</v-card-title>
            
            <v-data-table :headers="translatedHeaders" :items="reports" hover :items-per-page="-1" hide-default-footer>
              <template v-slot:item="{ item }">
                <tr class="custom-row" :style="{ '--row-bg-color': item.rowColor || 'transparent' }">
                  <td>
                    <v-checkbox v-model="selectedReports" :value="item.id" hide-details :disabled="!item.selectable"></v-checkbox>
                  </td>
                  <td>
                    <div class="py-2 d-flex align-start">
                      <div class="flex-grow-1">
                        <div class="text-subtitle-2 font-weight-bold">{{ item.nombre }}</div>
                        <div class="text-caption text-grey-darken-1">{{ item.descripcion }}</div>
                      </div>
                      <v-menu location="top" border>
                        <template v-slot:activator="{ props }">
                          <v-btn icon="mdi-information-outline" variant="text" size="small" color="blue-darken-1" v-bind="props" class="ml-2"></v-btn>
                        </template>
                        <v-card max-width="300" class="pa-3">
                          <div class="text-subtitle-2 font-weight-bold mb-1">{{ item.nombre }}</div>
                          <div class="text-body-2">
                            <div v-html="$t(`catalogo.escenarios.descripciones.${item.nombre}`, { default: item.descripcion })"></div>
                          </div>
                        </v-card>
                      </v-menu>
                    </div>
                  </td>
                  <td v-for="header in techHeaders" :key="header.key" class="pa-1">
                    <div 
                      v-if="hasTech(item, header.key)"
                      :class="['select-container', 'text-center', 'd-flex align-center justify-center', { 'highlight-border': !['mid', '0', 0].includes(item.config[header.key]) }]"
                      :style="{ backgroundColor: getColor2(item.config[header.key]), minHeight: '40px', borderRadius: '4px' }"
                    >
                      <span class="text-caption font-weight-bold">{{ item.config[header.key] }}</span>
                    </div>
                  </td>
                </tr>
              </template>
            </v-data-table>

            <v-checkbox v-model="escenarioPersonalizado" hide-details class="mt-4">
              <template v-slot:label>
                <span class="text-h5 font-weight-bold">{{ $t('catalogo.escenarios.personalizadoCheck') }}</span>
              </template>
            </v-checkbox>

            <v-expand-transition>
              <v-textarea
                v-if="escenarioPersonalizado"
                v-model="comentarioEscenario"
                :label="$t('catalogo.escenarios.comentarioLabel')"
                :placeholder="$t('catalogo.escenarios.comentarioPlaceholder')"
                rows="3"
                class="mt-2"
                outlined
              ></v-textarea>
            </v-expand-transition> 
          </v-card>
        </template>
          
        <v-divider></v-divider>

        <v-card-actions class="pa-6 bg-grey-lighten-5">
          <v-btn v-if="step > 1" variant="text" @click="step--">{{ $t('comun.anterior') }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="elevated" size="large" :disabled="!canNext" @click="nextStep">
            {{ step === 3 ? $t('comun.finalizar') : $t('comun.continuar') }}
          </v-btn>
        </v-card-actions>
      </v-stepper>
    </v-container>

    <!-- Dialogo de Solicitud -->
    <v-dialog v-model="dialogRequest" max-width="600px">
      <v-card class="rounded-lg">
        <v-card-title class="text-h6 bg-primary text-white pa-4">
          {{ $t('catalogo.dialogo.titulo') }}
        </v-card-title>
        
        <v-card-text class="pa-6">
          <div class="mb-6">
            <div class="text-subtitle-2 font-weight-bold mb-2">
              {{ $t('catalogo.dialogo.resumen', { zonas: zonas.join(', '), granularidad: $t(`catalogo.granularidadOpciones.${granularidad}`) }) }}:
            </div>
            <v-chip-group column>
              <v-chip v-for="report in selectedReportsObjects" :key="report.id" size="small" variant="outlined" color="primary">
                {{ report.nombre }}
              </v-chip>
              <v-chip v-if="escenarioPersonalizado" size="small" variant="flat" color="secondary">
                {{ $t('catalogo.escenarios.personalizadoLabel') }}
              </v-chip>
            </v-chip-group>
          </div>

          <v-select
            v-model="formData.periodicidad"
            :items="periodicidadItems"
            :label="$t('catalogo.formulario.periodicidad')"
            variant="outlined"
          ></v-select>

          <v-divider class="mb-6"></v-divider>
          <p class="text-body-2 mb-4 text-grey-darken-2">{{ $t('catalogo.formulario.instrucciones') }}</p>

          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field v-model="formData.nombre" :label="$t('catalogo.formulario.nombre')" variant="outlined" density="compact"></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model="formData.apellidos" :label="$t('catalogo.formulario.apellidos')" variant="outlined" density="compact"></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-text-field v-model="formData.email" :label="$t('catalogo.formulario.email')" variant="outlined" density="compact" prepend-inner-icon="mdi-email"></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-text-field v-model="formData.empresa" :label="$t('catalogo.formulario.empresa')" variant="outlined" density="compact"></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-select
                v-model="formData.perfil"
                :label="$t('catalogo.formulario.perfil')"
                :items="perfilItems"
                variant="outlined"
                density="compact"
              ></v-select>
            </v-col>
          </v-row>
          <p class="text-caption text-grey mt-2">🔒 {{ $t('catalogo.formulario.privacidad') }}</p>
        </v-card-text>

        <v-card-actions class="pa-4 bg-grey-lighten-5">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="dialogRequest = false">{{ $t('comun.cancelar') }}</v-btn>
          <v-btn color="primary" variant="elevated" :disabled="!formData.email || !formData.nombre" @click="submitRequest">
            {{ $t('catalogo.formulario.botonSolicitar') }}
            <v-icon end icon="mdi-arrow-right"></v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialogo de Éxito -->
    <v-dialog v-model="dialogSuccess" max-width="400px">
      <v-card class="text-center pa-6 rounded-lg">
        <v-icon color="success" size="64" class="mb-4">mdi-check-circle-outline</v-icon>
        <v-card-title class="text-h5 justify-center">{{ $t('catalogo.exito.titulo') }}</v-card-title>
        <v-card-actions class="justify-center mt-4">
          <v-btn color="primary" variant="flat" block @click="dialogSuccess = false">{{ $t('comun.cerrar') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import axios from 'axios';
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { markRaw } from 'vue';

export default {
  name: "LeafletMapSelector",
  data() {
    return {
      step: 1,
      map: null,
      geoJsonLayer: null,
      zonas: [],
      granularidad: null,
      selectedReports: [],
      headers: [],
      reports: [],
      optionsMap: [],
      dialogRequest: false,
      dialogSuccess: false,
      escenarioPersonalizado: false,
      comentarioEscenario: '',
      formData: {
        nombre: '',
        apellidos: '',
        email: '',
        empresa: '',
        perfil: null,
        periodicidad: 'Trimestral'
      }
    };
  },
  computed: {
    stepperItems() {
      return [
        this.$t('catalogo.pasos.zonas'),
        this.$t('catalogo.pasos.granularidad'),
        this.$t('catalogo.pasos.escenarios')
      ];
    },
    canNext() {
      if (this.step === 1) return this.zonas.length > 0;
      if (this.step === 2) return !!this.granularidad;
      if (this.step === 3) return this.selectedReports.length > 0 || this.escenarioPersonalizado;
      return false;
    },
    techHeaders() {
      const fixed = ['selection', 'nombre'];
      return this.headers.filter(h => h.key && !fixed.includes(h.key));
    },
    translatedHeaders() {
      return this.headers.map(h => ({
        ...h,
        title: h.title || this.$t(`catalogo.tabla.headers.${h.key}`, h.key)
      }));
    },
    zonasFiltradas() { return this.zonas.filter(z => z); },
    selectedReportsObjects() {
      return this.reports.filter(r => this.selectedReports.includes(r.id));
    },
    perfilItems() {
      const perfiles = ['desarrollador', 'utilities', 'fondo', 'industrial', 'consultoria', 'banco', 'otro'];
      return perfiles.map(p => ({ title: this.$t(`perfiles.${p}`), value: p }));
    },
    periodicidadItems() {
      const p = ['puntual','mensual','bimensual', 'trimestral', 'semestral'];
      return p.map(item => ({ title: this.$t(`comun.${item}`), value: item.charAt(0).toUpperCase() + item.slice(1) }));
    }
  },
  methods: {
    async initMap() {
      if (!this.$refs.mapContainer) {
        this.$nextTick(() => this.initMap());
        return;
      }
      if (this.map) {
        setTimeout(() => this.map.invalidateSize(), 100);
        return;
      }

      const leafletMap = L.map(this.$refs.mapContainer, { 
        attributionControl: false,
        zoomControl: true 
      }).setView([40, -3], 4);

      L.tileLayer('https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png').addTo(leafletMap);
      this.map = markRaw(leafletMap);

      try {
        const response = await fetch('https://raw.githubusercontent.com/leakyMirror/map-of-europe/master/GeoJSON/europe.geojson');
        const data = await response.json();
        this.geoJsonLayer = L.geoJSON(data, {
          style: (f) => ({
            fillColor: this.zonas.includes(this.translateName(f.properties.NAME)) ? "#3f51b5" : "#e2e8f0",
            weight: 1.5, opacity: 1, color: 'white', fillOpacity: 0.8
          }),
          onEachFeature: (feature, layer) => {
            layer.on({
              click: (e) => {
                L.DomEvent.stopPropagation(e);
                this.toggleZona(this.translateName(feature.properties.NAME), feature.properties.NAME);
              }
            });
          }
        }).addTo(this.map);
        setTimeout(() => this.map.invalidateSize(), 200);
      } catch (err) { console.error("Error GeoJSON", err); }
    },
    updateMapStyle() {
      if (this.geoJsonLayer) {
        this.geoJsonLayer.setStyle((f) => ({
          fillColor: this.zonas.includes(this.translateName(f.properties.NAME)) ? "#3f51b5" : "#e2e8f0"
        }));
      }
    },
    translateName(name) {
      const names = { "Spain": "España", "France": "Francia", "Portugal": "Portugal" };
      return names[name] || name;
    },
    toggleZona(translated, raw) {
      if (!["Spain", "France", "Portugal"].includes(raw)) return;
      const index = this.zonas.indexOf(translated);
      if (index === -1) this.zonas.push(translated);
      else this.zonas.splice(index, 1);
      this.updateMapStyle();
    },
    removeZona(name) {
      const index = this.zonas.indexOf(name);
      if (index !== -1) this.zonas.splice(index, 1);
      this.updateMapStyle();
    },
    hasTech(item, tech) {
      return item.config && Object.prototype.hasOwnProperty.call(item.config, tech);
    },
    getColor2(val) {
      const colors = { "vhigh": '#FFCDD2', "high": '#FFE0B2', "mid": "#FFF9C4", "low": '#C8E6C9', "10": '#FFCDD2', "5": '#FFE0B2', "3": '#C8E6C9' };
      return colors[val] || '#F5F5F5';
    },
    nextStep() {
      if (this.step === 3) this.dialogRequest = true;
      else this.step++;
    },
    async submitRequest() {
      try {

        //const token = ""
        const token = await this.$recaptcha('submit');

        /*
        if (!this.$recaptchaInstance) {
          throw new Error('reCAPTCHA no se ha inicializado correctamente.')
        }
        await this.$recaptchaInstance.recaptchaLoaded()
        const token = await this.$recaptchaInstance.executeRecaptcha('submit')
        */

        await axios.post(`${process.env.VUE_APP_API_BASE_URL}/api/solicitud`, {
          zonas: this.zonas,
          recaptcha_token: token,
          granularidad: this.granularidad,
          informes: this.selectedReportsObjects,
          email: this.formData.email,
          form: this.formData,
          custom: {
            escenario_personalizado: this.escenarioPersonalizado,
            descripcion: this.comentarioEscenario
          }
        });
        this.dialogRequest = false;
        this.dialogSuccess = true;
        this.resetForm();
      } catch (error) { console.error('Error:', error); }
    },
    resetForm() {
      this.selectedReports = [];
      this.zonas = [];
      this.granularidad = null;
      this.escenarioPersonalizado = false;
      this.comentarioEscenario = '';
      this.updateMapStyle();
      this.step = 1;
    },
    async fetchCatalogo() {
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/api/catalogo`);
        this.headers = res.data.headers;
        this.reports = res.data.scenarios;
        this.selectedReports = this.reports.filter(r => r.selectedByDefault).map(r => r.id);
      } catch (e) { console.error(e); }
    }
  },
  mounted() {
    this.fetchCatalogo();
    setTimeout(() => this.initMap(), 500);
  },
  watch: {
    step(newStep) {
      if (newStep === 1) {
        this.$nextTick(() => {
          setTimeout(() => { if (this.map) this.map.invalidateSize(); }, 300);
        });
      }
    }
  }
};
</script>

<style scoped>
.map-wrapper {
  position: relative;
  width: 100%;
  height: 350px;
  background: #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
}
.entsoe-map-container {
  height: 100%;
  width: 100%;
  z-index: 1;
}
:deep(.leaflet-container) {
  height: 100% !important;
  width: 100% !important;
}
.custom-row td { 
  background-color: var(--row-bg-color) !important; 
}
.select-container {
  transition: all 0.2s ease;
  border: 2px solid transparent;
}
.highlight-border {
  border: 2px solid #000;
  background-color: rgba(0, 0, 0, 0.05);
}
</style>